import { useEffect } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface BlogSchemaProps {
  title: string;
  description: string;
  datePublished: string;
  dateModified: string;
  faqs?: FAQItem[];
  imageUrl?: string;
  keywords?: string[];
}

export function BlogSchema({
  title,
  description,
  datePublished,
  dateModified,
  faqs = [],
  imageUrl = 'https://slim-file.com/favicon.png',
  keywords = []
}: BlogSchemaProps) {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": imageUrl,
    "datePublished": datePublished,
    "dateModified": dateModified,
    "author": {
      "@type": "Organization",
      "name": "SlimFile",
      "url": "https://www.slim-file.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "SlimFile",
      "logo": {
        "@type": "ImageObject",
        "url": "https://slim-file.com/favicon.png"
      }
    },
    "keywords": keywords.join(', '),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": typeof window !== 'undefined' ? window.location.href : 'https://www.slim-file.com'
    }
  };

  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // Added straight to <head>; the app has no Helmet provider.
  const json = JSON.stringify([articleSchema, faqSchema].filter(Boolean));
  useEffect(() => {
    const scripts = (JSON.parse(json) as object[]).map(schema => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => scripts.forEach(el => el.remove());
  }, [json]);

  return null;
}

// FAQ Component for rendering FAQs visually
interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
}

export function FAQSection({ faqs, title = "Frequently Asked Questions" }: FAQSectionProps) {
  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">{title}</h2>
        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{faq.question}</h3>
              <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
